import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ActualDto, CreateActualInput, PositionDetail } from "../../lib/cashflow";
import { Modal } from "../ui/Modal";

interface ActualFormModalProps {
  position: PositionDetail;
  defaultMonth?: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (input: CreateActualInput) => Promise<void>;
  onReplace: (existing: ActualDto, input: CreateActualInput) => Promise<void>;
}

function parseEuroDe(input: string): number {
  const trimmed = input.replace(/\./g, "").replace(",", ".").trim();
  const n = Number.parseFloat(trimmed);
  if (Number.isNaN(n) || n <= 0) throw new Error("invalid_amount");
  return Math.round(n * 100);
}

function smartDefaultMonth(position: PositionDetail, defaultMonth?: string): string {
  if (defaultMonth) return defaultMonth;
  const now = new Date();
  const current = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
  // First try current month if no actual yet and inside range
  const hasCurrent = position.actuals.some((a) => a.month === current);
  if (
    !hasCurrent &&
    current >= position.firstDueMonth &&
    (!position.endMonth || current <= position.endMonth)
  ) {
    return current;
  }
  // Otherwise: youngest past actual-less month — naive walk back 24 months
  for (let i = 1; i <= 24; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const ym = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
    if (ym < position.firstDueMonth) break;
    const hasIt = position.actuals.some((a) => a.month === ym);
    if (!hasIt) return ym;
  }
  return "";
}

export function ActualFormModal({
  position,
  defaultMonth,
  open,
  onClose,
  onSubmit,
  onReplace,
}: ActualFormModalProps) {
  const { t } = useTranslation();
  const [month, setMonth] = useState(() => smartDefaultMonth(position, defaultMonth));
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const existingForMonth = useMemo(
    () => position.actuals.find((a) => a.month === month),
    [position.actuals, month],
  );

  async function handleSubmit(replace: boolean) {
    setServerError(null);
    let actualCents: number;
    try {
      actualCents = parseEuroDe(amount);
    } catch {
      setServerError(t("cashflow.errors.invalid_amount"));
      return;
    }
    setSubmitting(true);
    try {
      const input: CreateActualInput = { month, actualCents };
      if (note.trim()) input.note = note.trim();
      if (replace && existingForMonth) {
        await onReplace(existingForMonth, input);
      } else {
        await onSubmit(input);
      }
      onClose();
    } catch (err) {
      setServerError(err instanceof Error ? err.message : "errors.unknown");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={t("cashflow.actual.title")}>
      <div className="space-y-3">
        <label className="block">
          <span className="text-sm">{t("cashflow.actual.label.month")}</span>
          <input
            type="month"
            className="border rounded px-2 py-1"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            min={position.firstDueMonth}
            max={position.endMonth ?? undefined}
          />
        </label>
        <label className="block">
          <span className="text-sm">{t("cashflow.actual.label.amount")}</span>
          <input
            className="border rounded px-2 py-1 w-full"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-sm">{t("cashflow.actual.label.note")}</span>
          <input
            className="border rounded px-2 py-1 w-full"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={200}
          />
        </label>

        {existingForMonth && (
          <div className="border border-amber-300 bg-amber-50 rounded p-2 text-sm">
            {t("cashflow.actual.duplicate_month_hint", { month })}
          </div>
        )}

        {serverError && <div className="text-red-700 text-sm">{serverError}</div>}

        <div className="flex gap-2 justify-end">
          <button
            type="button"
            className="px-3 py-1 border rounded"
            onClick={onClose}
            disabled={submitting}
          >
            {t("common.cancel")}
          </button>
          {existingForMonth ? (
            <button
              type="button"
              className="px-3 py-1 bg-amber-600 text-white rounded"
              disabled={submitting}
              onClick={() => handleSubmit(true)}
            >
              {t("cashflow.actual.replace")}
            </button>
          ) : (
            <button
              type="button"
              className="px-3 py-1 bg-blue-600 text-white rounded"
              disabled={submitting}
              onClick={() => handleSubmit(false)}
            >
              {t("common.save")}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
